import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowRight } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RoleBadge } from "@/components/RoleBadge";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function AcceptInvite() {
  const { token } = useParams();
  const { t } = useTranslation(["auth", "common"]);
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let active = true;
    api
      .get(`/invites/${token}`)
      .then(({ data }) => {
        if (!active) return;
        setPreview(data);
      })
      .catch((err) => {
        if (!active) return;
        const status = err?.response?.status;
        if (status === 410) setPreviewError(t("auth:invite.expired"));
        else if (status === 404) setPreviewError(t("auth:invite.invalidInvite"));
        else setPreviewError(formatApiError(err, t("auth:invite.invalidInvite")));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    try {
      await acceptInvite(token, { name: name.trim(), password });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410) setSubmitError(t("auth:invite.expired"));
      else if (status === 409) setSubmitError(t("auth:invite.alreadyUsed"));
      else setSubmitError(formatApiError(err, t("common:errors.unexpected")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className="space-y-8" data-testid="invite-page">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {t("auth:invite.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t("auth:invite.subtitle")}</p>
        </div>

        {loading && (
          <div data-testid="invite-loading" className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("common:loading")}
          </div>
        )}

        {!loading && previewError && (
          <p
            data-testid="invite-preview-error"
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {previewError}
          </p>
        )}

        {!loading && preview && (
          <>
            <div
              data-testid="invite-preview"
              className="rounded-xl border border-slate-200 bg-slate-50/60 p-4"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {t("auth:invite.joinAs", {
                  tenant: preview.tenant_name,
                  role: t(`common:roles.${preview.role}`),
                })}
              </p>
              <div className="mt-2 flex items-center justify-between gap-3">
                <span data-testid="invite-email" className="text-sm font-medium text-slate-900">
                  {preview.email}
                </span>
                <RoleBadge role={preview.role} />
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-5" noValidate>
              <div className="space-y-2">
                <Label htmlFor="inv-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("auth:invite.nameLabel")}
                </Label>
                <Input
                  id="inv-name"
                  data-testid="invite-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("auth:invite.namePlaceholder")}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inv-password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("auth:invite.passwordLabel")}
                </Label>
                <Input
                  id="inv-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="invite-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth:invite.passwordPlaceholder")}
                  className="h-11"
                />
              </div>

              {submitError && (
                <p
                  data-testid="invite-error"
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {submitError}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                data-testid="invite-submit-button"
                className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {t("auth:invite.submit")}
                    <ArrowRight className="ml-1 h-4 w-4" strokeWidth={2} />
                  </>
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </AuthShell>
  );
}
